"use client";

import { useEffect, useState, useCallback, useMemo } from "react";
import { useSocket } from "./SocketProvider";

interface Bid {
  historyBidId: number;
  historyBidValue: number;
  historyBidUserId: number;
  [key: string]: any;
}

interface LiveItem {
  itemId: number;
  bids: Bid[];
  itemEndTime: string;
  serverTime: string;
  leadingAutoBid: boolean;
}

export default function useLiveBids({
  auctionItemIds,
  auctionPartId,
}: {
  auctionItemIds?: number[];
  auctionPartId?: number;
} = {}) {
  const socket = useSocket();
  const [auctionItemBidsLive, setAuctionItemBidsLive] = useState<LiveItem[]>(
    []
  );

  const itemIdsSet = useMemo(
    () => new Set(auctionItemIds || []),
    [auctionItemIds]
  );

  useEffect(() => {
    if (!socket) {
      setAuctionItemBidsLive([]);
      return;
    }

    const bidHandler = (response: any) => {
      const bid = response?.bid;
      const item = response?.item;

      if (!bid || !item) return;

      const match =
        (auctionItemIds && itemIdsSet.has(item.itemId)) ||
        (auctionPartId && item.itemPartId === auctionPartId);

      if (!match) return;

      setAuctionItemBidsLive((prev: LiveItem[]) => {
        const existing = prev.find(
          (entry: LiveItem) => entry.itemId === item.itemId
        );

        if (existing) {
          const alreadyExists = existing.bids.some(
            (b: Bid) => b.historyBidId === bid.historyBidId
          );

          if (alreadyExists) return prev;

          return prev.map((entry: LiveItem) =>
            entry.itemId === item.itemId
              ? {
                  ...entry,
                  bids: [bid, ...entry.bids],
                  itemEndTime: item.itemEndTime,
                  serverTime: new Date().toISOString(),
                  leadingAutoBid: item.leadingAutoBid,
                }
              : entry
          );
        }

        return [
          ...prev,
          {
            itemId: item.itemId,
            bids: [bid],
            itemEndTime: item.itemEndTime,
            serverTime: new Date().toISOString(),
            leadingAutoBid: item.leadingAutoBid,
          },
        ];
      });
    };

    // Listen for new_bid events (existing)
    socket.on("new_bid", bidHandler);

    // Listen for item_update events (new)
    const itemUpdateHandler = (message: any) => {
      const auctionItem = message?.auctionItem;
      const meta = message?.meta;
      const leadingAutoBid = meta?.leadingAutoBid;

      console.log("before update", message);
      if (!auctionItem) return;

      const match =
        (auctionItemIds && itemIdsSet.has(Number(auctionItem.itemId))) ||
        (auctionPartId && Number(auctionItem.itemPartId) === auctionPartId);

      if (!match) return;

      console.log("trigger update 1");

      setAuctionItemBidsLive((prev: LiveItem[]) => {
        console.log("trigger update 1 a", prev);
        const existing = prev.find(
          (entry: LiveItem) => entry.itemId === auctionItem.itemId
        );
        console.log("trigger NOT existing");
        if (existing) {
          console.log("trigger update existing", existing, leadingAutoBid);

          // Only update if leadingAutoBid has changed
          if (existing.leadingAutoBid !== leadingAutoBid) {
            console.log("trigger update existing 2", leadingAutoBid);

            return prev.map((entry: LiveItem) =>
              entry.itemId === auctionItem.itemId
                ? {
                    ...entry,
                    leadingAutoBid,
                    serverTime: meta?.serverTime || new Date().toISOString(),
                  }
                : entry
            );
          }
          return prev;
        }
        // If not existing, add new
        return [
          ...prev,
          {
            itemId: auctionItem.itemId,
            bids: [],
            itemEndTime: auctionItem.itemEndTime,
            serverTime: meta?.serverTime || new Date().toISOString(),
            leadingAutoBid,
          },
        ];
      });
    };

    socket.on("item_update", itemUpdateHandler);

    return () => {
      socket.off("new_bid", bidHandler);
      socket.off("item_update", itemUpdateHandler);
    };
  }, [socket, itemIdsSet, auctionPartId]);

  const mergeWithInitialBids = useCallback(
    (initial: any[]) => {
      if (!initial || !Array.isArray(initial)) return [];

      return initial.map((item) => {
        const live = auctionItemBidsLive.find((l) => l.itemId === item.itemId);
        if (!live) return item;

        const existingBidIds = new Set(
          item.bids?.map((b: Bid) => b.historyBidId) || []
        );
        const newBids = live.bids.filter(
          (b) => !existingBidIds.has(b.historyBidId)
        );

        console.log("bidData - live.leadingAutoBid", live.leadingAutoBid);

        return {
          ...item,
          bids: [...newBids, ...(item.bids || [])],
          itemEndTime: live.itemEndTime,
          serverTime: live.serverTime,
          leadingAutoBid: live.leadingAutoBid,
        };
      });
    },
    [auctionItemBidsLive]
  );

  return { auctionItemBidsLive, mergeWithInitialBids };
}
